import policyCompany from "./policy.company.mjs";

export const privacyPolicy = {
  id: "privacy-policy",
  route: "/privacy-policy",
  title: "Website Privacy Policy",
  subtitle: "Privacy Policy",
  fileName: "DCAR Website Privacy Policy.pdf",
  version: "1.0",
  effectiveDate: "4 September 2026",
  company: policyCompany,
  summary:
    "What personal data DCAR collects on the website, why we collect it, who we share it with, and the rights you have under the Personal Data Protection Act 2010.",
  intro: [
    `${policyCompany.legalName} (Company No. ${policyCompany.registrationNo}), trading as ${policyCompany.brand} ("we", "us", "our"), respects your privacy. This Privacy Policy explains how we collect, use, disclose and protect personal data when you use ${policyCompany.website}, our mobile web experience, and the services offered through them.`,
    "We process personal data in accordance with the Personal Data Protection Act 2010 of Malaysia (\"PDPA\"). By using the website or submitting information to us, you confirm that you have read and understood this Policy.",
  ],
  sections: [
    {
      heading: "1. Who this policy covers",
      paragraphs: [
        "This Policy applies to buyers browsing or reserving vehicles, owners consigning a vehicle for sale, users booking workshop or inspection services, and anyone who contacts us through the website.",
        "It does not cover third-party websites, payment gateways, financiers or insurers that you are directed to. Those parties operate under their own privacy notices.",
      ],
    },
    {
      heading: "2. Personal data we collect",
      paragraphs: [
        "We collect only what we need to run the marketplace and complete a vehicle transaction. Depending on how you use the website, this may include:",
      ],
      bullets: [
        {
          label: "Account and contact data",
          text: "Mobile number, one-time verification codes, name, email address and profile preferences, together with your saved cars, search history and language setting.",
        },
        {
          label: "Identity and verification data",
          text: "For a purchase we collect your legal first and last name, NRIC or passport number, residential address, city, postcode and state, and images of your identity card and driving licence. For company buyers we collect the company name, registration number, company email, tax identification number and SSM documents.",
        },
        {
          label: "Vehicle and consignment data",
          text: "Vehicle registration number, make, model, year, engine and chassis numbers, engine capacity, odometer reading, vehicle photographs, the vehicle grant (geran), NCD letters and any description you supply.",
        },
        {
          label: "Transaction data",
          text: "Reservation and order records, reservation fees, agreed selling price, payment channel selected, payment status and reference numbers, and refund records.",
        },
        {
          label: "Financing and insurance data",
          text: "Where you apply through us: financing preferences, insurer, sum insured, no-claim discount, windscreen and accessory coverage, and supporting documents required by the financier or insurer.",
        },
        {
          label: "Documents and signatures",
          text: "Signed Vehicle Sales Orders, service agreements and sale and purchase agreements, including the handwritten signature you draw on screen and the date of signing.",
        },
        {
          label: "Inspection data",
          text: "Inspection reports, condition grades, checklists and photographs produced by our inspection partners for a vehicle you list or reserve.",
        },
        {
          label: "Technical and usage data",
          text: "IP address, device and browser type, operating system, referring pages, pages viewed, session identifiers and the authentication token stored in your browser.",
        },
        {
          label: "Communications",
          text: "Enquiries, appointment requests, WhatsApp, email and call records, and feedback or complaints you send us.",
        },
      ],
    },
    {
      heading: "3. How we collect personal data",
      bullets: [
        "Directly from you when you register, log in with a one-time password, complete a form, upload a document, sign a document on screen, or contact our team.",
        "Automatically through cookies, local storage and similar technologies when you browse the website.",
        "From third parties such as vehicle inspection partners, payment gateway providers, financiers, insurers and workshop partners involved in your transaction.",
        "From publicly available sources, where we need to verify vehicle or company information.",
      ],
    },
    {
      heading: "4. Why we use personal data",
      bullets: [
        "To create and manage your account and authenticate you by one-time password.",
        "To publish, manage and match vehicle listings, saved cars and enquiries.",
        "To process reservations, orders, payments and refunds, and to issue receipts and confirmations.",
        "To carry out know-your-customer and anti-fraud checks required before ownership transfer.",
        "To prepare, generate and store the transaction documents you sign with us.",
        "To submit financing and insurance applications on your instruction.",
        "To arrange inspections, viewings, workshop appointments and vehicle handover.",
        "To operate the referral programme and issue any reward you earn.",
        "To provide customer support and respond to your enquiries and complaints.",
        "To improve the website, monitor performance, and keep the platform secure.",
        "To send you service messages, and — where you have consented — marketing about vehicles and offers.",
        "To comply with legal, tax, regulatory and road transport requirements in Malaysia.",
      ],
    },
    {
      heading: "5. Consent and the consequences of not supplying data",
      paragraphs: [
        "Some data is mandatory. If you do not supply your mobile number we cannot create an account for you, and if you do not supply identity, address or ownership documents we cannot complete a reservation, sale, financing application or ownership transfer. Data marked as optional may be withheld without affecting your access to the rest of the platform.",
        "Where we rely on your consent — for example for marketing messages — you may withdraw that consent at any time using the contact details in Section 14.",
      ],
    },
    {
      heading: "6. Who we disclose personal data to",
      paragraphs: [
        "We do not sell personal data. We disclose it only where it is necessary for the purposes above, to:",
      ],
      bullets: [
        "The registered owner or buyer of the vehicle involved in your transaction, limited to what is needed to complete the sale.",
        "Payment gateway and banking partners that process your reservation fee, instalments and refunds.",
        "Financiers, hire purchase providers and insurance companies you apply to through us.",
        "Vehicle inspection partners, workshops, transporters and valuation partners engaged for your vehicle.",
        "Professional advisers, auditors and insurers of our own business.",
        "IT, hosting, analytics, messaging and customer support vendors acting as our data processors under contract.",
        "Government agencies, regulators, road transport and law enforcement authorities where required by law or to establish or defend legal claims.",
        "A successor entity in the event of a corporate restructuring, merger or sale of business.",
      ],
    },
    {
      heading: "7. Transfers outside Malaysia",
      paragraphs: [
        "Some of our service providers — for example cloud hosting, messaging and analytics providers — process data on servers located outside Malaysia. Where that happens we take reasonable steps to ensure the recipient provides a level of protection comparable to the PDPA, including contractual safeguards.",
      ],
    },
    {
      heading: "8. Cookies and browser storage",
      paragraphs: [
        "We use cookies and browser storage to keep you signed in, remember your language and filter preferences, keep your saved cars and cart in sync, and measure how the website is used. Your login token is stored in your browser's local storage and is removed when you log out.",
        "You can block or delete cookies through your browser settings. If you do, parts of the website — including login, reservations and saved cars — may stop working correctly.",
      ],
    },
    {
      heading: "9. How long we keep personal data",
      bullets: [
        {
          label: "Account data",
          text: "Kept for as long as your account is active, and for up to 24 months after your last activity.",
        },
        {
          label: "Transaction and document records",
          text: "Kept for at least 7 years after the transaction, to meet accounting, tax and statutory record-keeping obligations in Malaysia.",
        },
        {
          label: "Marketing preferences",
          text: "Kept until you withdraw consent, plus a suppression record so that we do not contact you again in error.",
        },
        {
          label: "Technical logs",
          text: "Kept for a shorter period appropriate to security monitoring and troubleshooting.",
        },
      ],
    },
    {
      heading: "10. Security",
      paragraphs: [
        "We apply organisational and technical measures appropriate to the sensitivity of the data, including encrypted transport (HTTPS), access controls, authenticated sessions, and restricted internal access to identity documents. Card and bank credentials are entered on our payment providers' pages and are not stored by us.",
        "No system is completely secure. You are responsible for keeping your device and the one-time passwords sent to your mobile number confidential.",
      ],
    },
    {
      heading: "11. Your rights under the PDPA",
      bullets: [
        {
          label: "Access",
          text: "You may request a copy of the personal data we hold about you.",
        },
        {
          label: "Correction",
          text: "You may ask us to correct data that is inaccurate, incomplete or out of date.",
        },
        {
          label: "Withdraw consent",
          text: "You may withdraw consent to any processing that relies on consent, including marketing.",
        },
        {
          label: "Limit processing",
          text: "You may ask us to limit processing for direct marketing or for a specific purpose.",
        },
        {
          label: "Enquiries and complaints",
          text: "You may ask how your data has been used and lodge a complaint with us or with the Personal Data Protection Commissioner.",
        },
      ],
      paragraphs: [
        "We may need to verify your identity before acting on a request, and a reasonable fee may apply to an access request as permitted by the PDPA. We aim to respond within 21 days.",
      ],
    },
    {
      heading: "12. Third-party links and content",
      paragraphs: [
        "The website links to third-party sites and embeds content such as maps, payment pages and social media. We are not responsible for the privacy practices of those parties, and we encourage you to read their notices before submitting information to them.",
      ],
    },
    {
      heading: "13. Children",
      paragraphs: [
        "The website and services are intended for individuals aged 18 and above. We do not knowingly collect personal data from anyone under 18. If you believe a minor has supplied us with personal data, contact us and we will delete it.",
      ],
    },
    {
      heading: "14. Contact us",
      paragraphs: [
        "Requests, questions and complaints about this Policy or about your personal data can be addressed to our Data Protection Officer:",
      ],
      bullets: [
        { label: "Email", text: policyCompany.email },
        { label: "Phone", text: policyCompany.phone },
        {
          label: "Address",
          text: `${policyCompany.legalName}, ${policyCompany.address.join(" ")}`,
        },
      ],
    },
    {
      heading: "15. Changes to this policy",
      paragraphs: [
        "We may update this Policy as our services or the law change. The effective date at the top of this page shows when it was last revised, and material changes will be notified on the website or by message to your registered mobile number.",
        "In the event of any inconsistency between the English version and a translation of this Policy, the English version prevails.",
      ],
    },
  ],
};

export default privacyPolicy;
